import { useCallback, useEffect, useState } from 'react';
import { useMuvelet } from '../felhasznalo-context.ts';
import { TorzsOsszevetes } from './TorzsOsszevetes.tsx';

import { api, type AutomentesAllapot, type MentesekValasz } from '../api.ts';
import { datum as datumFormaz, szam } from '../formazas.ts';

/**
 * Az automatikus napi mentés állapota és beállításai. (2026-09-17)
 *
 * A program naponta magától ment, és a mentést egy második helyre (másik
 * lemez vagy hálózati meghajtó) is átmásolja. Itt látszik, mikor készült a
 * legutóbbi, sikerült-e a másolás, és itt írható át a második hely.
 */
function AutomentesPanel({
  allapot,
  onFrissit,
}: {
  allapot: AutomentesAllapot;
  onFrissit: () => Promise<void>;
}) {
  const [mappa, setMappa] = useState(allapot.masodik_mappa ?? '');
  const [megtart, setMegtart] = useState(String(allapot.megtart_napok));
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const [munkaban, setMunkaban] = useState(false);

  async function ment() {
    setMunkaban(true);
    setHiba(null);
    setUzenet(null);
    try {
      const uj = await api.patch<AutomentesAllapot>('/api/export/automentes', {
        masodik_mappa: mappa,
        megtart_napok: Number(megtart),
      });
      setUzenet(uj.masolas_hiba ? null : 'Beállítás elmentve.');
      await onFrissit();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A beállítás nem sikerült.');
    } finally {
      setMunkaban(false);
    }
  }

  async function mostMent() {
    setMunkaban(true);
    setHiba(null);
    setUzenet(null);
    try {
      const v = await api.post<{ uj: string | null }>('/api/export/automentes');
      setUzenet(v.uj ? `Elkészült: ${v.uj}` : 'Ma már készült automatikus mentés.');
      await onFrissit();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A mentés nem sikerült.');
    } finally {
      setMunkaban(false);
    }
  }

  const rendben = !allapot.masolas_hiba && allapot.utolso_kora_nap !== null && allapot.utolso_kora_nap <= 1;
  return (
    <div className={`uzenet ${allapot.masolas_hiba ? 'bad' : rendben ? 'ok' : 'flag'}`}>
      <b>Automatikus napi mentés.</b>{' '}
      {allapot.utolso ? (
        <>
          Legutóbbi: {datumFormaz(allapot.utolso.keszult)}{' '}
          {new Date(allapot.utolso.keszult).toLocaleTimeString('hu-HU')}
          {allapot.utolso_kora_nap ? ` (${allapot.utolso_kora_nap} napja)` : ' (ma)'}.
        </>
      ) : (
        'Még nem készült — a program indulás után pár másodperccel elkészíti.'
      )}{' '}
      {allapot.masolas_hiba ? (
        <>A második helyre NEM sikerült másolni: {allapot.masolas_hiba}</>
      ) : allapot.masodik_mappa ? (
        <>A mentés a második helyre is átkerül: <code>{allapot.masodik_mappa}</code>.</>
      ) : (
        <>Nincs megadva második hely — a mentés csak ezen a gépen van.</>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
        <label className="hint">Második hely</label>
        <input
          className="qi"
          style={{ width: 300, fontSize: 12.5 }}
          value={mappa}
          placeholder="pl. O:\GERGŐ\Jaratlap-mentesek"
          onChange={(e) => setMappa(e.target.value)}
        />
        <label className="hint">Megtartott napok</label>
        <input
          className="qi"
          style={{ width: 60, fontSize: 12.5 }}
          value={megtart}
          onChange={(e) => setMegtart(e.target.value)}
        />
        <button className="btn sm" disabled={munkaban} onClick={() => void ment()}>
          Beállítás mentése
        </button>
        <button className="btn sm" disabled={munkaban} onClick={() => void mostMent()}>
          Napi mentés most
        </button>
      </div>
      {hiba && <div style={{ color: 'var(--bad)', marginTop: 6 }}>{hiba}</div>}
      {uzenet && <div style={{ marginTop: 6 }}>{uzenet}</div>}
      <div className="hint" style={{ marginTop: 6 }}>
        Az utolsó {allapot.megtart_napok} nap mentése marad meg, a régebbiekből hónaponként egy.
        A kézi, a frissítés előtti és az évzárási mentéseket a program soha nem törli.
      </div>
    </div>
  );
}

/**
 * Biztonsági mentés és visszaállítás. (Megbízás 7.4)
 *
 * A mentés az egész adatbázis egyetlen dátumozott fájlban. Írás közben is
 * készíthető, nem kell hozzá leállítani a programot.
 *
 * A visszaállítás előtt a program **magától készít egy mentést a jelenlegi
 * állapotról** — így rossz választás esetén sem vész el semmi.
 */
export function Mentesek() {
  const menthet = useMuvelet('mentes');
  /** Melyik mentéssel vetjük össze a partnertörzset. (2026-10-09) */
  const [osszevetes, setOsszevetes] = useState<string | null>(null);
  const [adat, setAdat] = useState<MentesekValasz | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const [munkaban, setMunkaban] = useState(false);
  const [megjegyzes, setMegjegyzes] = useState('');

  const betolt = useCallback(async () => {
    try {
      setAdat(await api.get<MentesekValasz>('/api/export/mentesek'));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni a mentéseket.');
    }
  }, []);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  async function mentes() {
    setMunkaban(true);
    setHiba(null);
    setUzenet(null);
    try {
      const v = await api.post<{ mentes: { nev: string; meret: number } }>('/api/export/mentes', {
        megjegyzes: megjegyzes.trim() || undefined,
      });
      setUzenet(
        `Mentés kész: ${v.mentes.nev} (${szam(v.mentes.meret / 1024, 0)} kB). ` +
          'Érdemes időnként egy pendrive-ra vagy másik gépre is átmásolni.',
      );
      setMegjegyzes('');
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A mentés nem sikerült.');
    } finally {
      setMunkaban(false);
    }
  }

  async function visszaallit(nev: string) {
    const megerosites = prompt(
      `Biztosan visszaállítod ezt a mentést?\n\n${nev}\n\n` +
        'A mostani adatok helyére a mentés kerül. A program előbb elmenti a\n' +
        'jelenlegi állapotot, majd újraindul.\n\n' +
        'Ha biztos vagy benne, írd be: VISSZAALLIT',
    );
    if (megerosites !== 'VISSZAALLIT') return;

    setMunkaban(true);
    setHiba(null);
    try {
      const v = await api.post<{ elozo_mentes: string }>('/api/export/visszaallitas', { nev });
      setUzenet(
        `A visszaállítás megtörtént. A jelenlegi állapotról készült mentés: ${v.elozo_mentes}. ` +
          'A program most újraindul — pár másodperc múlva frissítsd az oldalt.',
      );
      // A szerver leáll és újraindul; megvárjuk, míg újra válaszol.
      setTimeout(() => location.reload(), 6000);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A visszaállítás nem sikerült.');
      setMunkaban(false);
    }
  }

  // Akinek nincs rá engedélye, annak a felület sem kínálja fel. (3.0 / 1.)
  if (!menthet) {
    return <div className="hint">A biztonsági mentés és visszaállítás külön engedélyhez kötött. Szólj az adminisztrátornak.</div>;
  }

  return (
    <>
      <div className="cards">
        <div className="card">
          <em>Adatfájl mérete</em>
          <b>{szam((adat?.adatbazis.meret ?? 0) / 1024, 0)}</b>
          <span>kB</span>
        </div>
        <div className="card">
          <em>Étterem</em>
          <b>{adat?.adatbazis.ettermek ?? 0}</b>
          <span>a törzsben</span>
        </div>
        <div className="card">
          <em>Rögzített nap</em>
          <b>{adat?.adatbazis.napok ?? 0}</b>
          <span>járatlap</span>
        </div>
        <div className="card">
          <em>Mentés</em>
          <b>{adat?.mentesek.length ?? 0}</b>
          <span>db</span>
        </div>
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      {osszevetes && (
        <TorzsOsszevetes
          mentesNev={osszevetes}
          onKesz={(u) => {
            setOsszevetes(null);
            setUzenet(u);
          }}
          onMegse={() => setOsszevetes(null)}
        />
      )}

      <div className="actbar">
        <input
          className="qi"
          style={{ width: 230, fontSize: 12.5 }}
          placeholder="megjegyzés a mentéshez (nem kötelező)"
          value={megjegyzes}
          onChange={(e) => setMegjegyzes(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void mentes()}
        />
        <button className="btn pri" disabled={munkaban} onClick={() => void mentes()}>
          {munkaban ? 'Dolgozom…' : 'Biztonsági mentés készítése'}
        </button>
        <span className="hint">
          A mentések helye: <code>{adat?.mappa ?? 'adat\\mentesek'}</code>
        </span>
      </div>

      {adat && <AutomentesPanel allapot={adat.automentes} onFrissit={betolt} />}

      <div className="matrix">
        <table className="tt2">
          <thead>
            <tr>
              <th>Mentés</th>
              <th className="n">Méret</th>
              <th>Készült</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(adat?.mentesek ?? []).map((m) => (
              <tr key={m.nev}>
                <td style={{ padding: '8px 10px', fontFamily: 'var(--mono)', fontSize: 12 }}>
                  {m.nev}
                </td>
                <td className="calc2">{szam(m.meret / 1024, 0)} kB</td>
                <td style={{ padding: '8px 10px', color: 'var(--muted)' }}>
                  {datumFormaz(m.keszult)} {new Date(m.keszult).toLocaleTimeString('hu-HU')}
                </td>
                <td className="act" style={{ width: 250, whiteSpace: 'nowrap' }}>
                  {/*
                    * A törzs összevetése: csak a partnerek azonosító adatait
                    * hozza vissza, a napokat és az okmányokat nem bántja.
                    * Ez a szelídebb út, ezért áll elöl.
                    * (2026-10-09, a megrendelő jelzése)
                    */}
                  <button
                    className="btn sm"
                    disabled={munkaban}
                    title="Megnézi, melyik partner azonosító adata tér el ettől a mentéstől"
                    onClick={() => setOsszevetes(m.nev)}
                  >
                    Törzs összevetése
                  </button>{' '}
                  <button
                    className="btn sm"
                    disabled={munkaban}
                    title="A teljes adatbázist visszaállítja — a napokat és az okmányokat is"
                    onClick={() => void visszaallit(m.nev)}
                  >
                    Teljes visszaállítás
                  </button>
                </td>
              </tr>
            ))}
            {(adat?.mentesek.length ?? 0) === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  Még nincs mentés. A fenti gombbal készíthetsz egyet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
